/**
 * assets/vehicle-finder.js  v3.2
 * Custom Element <vehicle-finder> — Dawn 5.0.0
 *
 * Flujo:
 *   init    → GET /Make          → popula Make (548 marcas)
 *           → popula Tipo de producto y Marca (VF_PRODUCT_TYPES / VF_VENDORS, listas estáticas)
 *   Make    → GET /Model         → popula Model
 *   Model   → GET /BaseVehicle   → extrae años
 *   Submit  → redirect a /search?q="marca modelo año"&type=product
 *             &filter.p.product_type=...&filter.p.vendor=... (si se seleccionaron)
 */

const VF_API_BASE  = 'https://car.refa100.shop/items';
const VF_API_TOKEN = 'fEqy1anyVBGTqPFRF7hNTK-iHeUq7S3v';

// ─── Caché sessionStorage ────────────────────────────────────────────────────

const VFCache = {
  get(key) {
    try {
      const raw = sessionStorage.getItem('vf__' + key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  set(key, value) {
    try { sessionStorage.setItem('vf__' + key, JSON.stringify(value)); } catch {}
  }
};

// ─── Fetch con auth + caché ──────────────────────────────────────────────────

async function vfFetch(endpoint) {
  const cached = VFCache.get(endpoint);
  if (cached) return cached;

  const res = await fetch(VF_API_BASE + endpoint, {
    headers: {
      'Authorization': 'Bearer ' + VF_API_TOKEN,
      'Accept': 'application/json'
    }
  });

  if (!res.ok) throw new Error('API error ' + res.status + ': ' + endpoint);

  const json = await res.json();
  VFCache.set(endpoint, json);
  return json;
}

// ─── Traducciones ────────────────────────────────────────────────────────────

function vfT(key) {
  const fallback = {
    select_make:  'Selecciona marca',
    select_model: 'Selecciona modelo',
    select_year:  'Selecciona año',
    loading:      'Cargando...',
    find_parts:   'Buscar partes',
    error:        'Ocurrió un error. Intenta de nuevo.'
  };
  return (window.VehicleFinderI18n && window.VehicleFinderI18n[key]) || fallback[key] || key;
}

// ─── Custom Element ──────────────────────────────────────────────────────────

class VehicleFinder extends HTMLElement {

  connectedCallback() {
    this.config  = window.VehicleFinderConfig || {};
    this.$make   = this.querySelector('[data-vf-make]');
    this.$model  = this.querySelector('[data-vf-model]');
    this.$year   = this.querySelector('[data-vf-year]');
    this.$submit = this.querySelector('[data-vf-submit]');
    this.$error  = this.querySelector('[data-vf-error]');
    this._yearMap = {};
    this.state   = {
      makeId: null, makeName: '',
      modelId: null, modelName: '',
      year: '', vehicleCode: ''
    };

    this.bindEvents();
    this.loadMakes();
  }

  // ─── Carga de datos ──────────────────────────────────────────────────────

  async loadMakes() {
    try {
      const { data } = await vfFetch('/Make?limit=-1&meta=total_count');
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      this.populateSelect(this.$make, sorted.map(m => ({
        value: m.reg,
        label: m.name
      })));
      this.$make.disabled = false;
    } catch (err) {
      console.error('[VehicleFinder] Error cargando marcas:', err);
      this.showError();
    }
  }

  async loadModels(makeId) {
    this.resetSelect(this.$model, vfT('select_model'));
    this.resetSelect(this.$year,  vfT('select_year'));
    this.$model.disabled  = true;
    this.$year.disabled   = true;
    this.$submit.disabled = true;

    if (!makeId) return;

    this.$model.options[0].textContent = vfT('loading');

    try {
      const { data } = await vfFetch('/Model?filter[makeId][_eq]=' + makeId + '&limit=-1&meta=filter_count');
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      this.populateSelect(this.$model, sorted.map(m => ({
        value: m.id,
        label: m.name
      })));
      this.$model.disabled = false;
    } catch (err) {
      console.error('[VehicleFinder] Error cargando modelos:', err);
      this.showError();
    }
  }

  async loadYears(makeId, modelId) {
    this.resetSelect(this.$year, vfT('select_year'));
    this.$year.disabled   = true;
    this.$submit.disabled = true;

    if (!makeId || !modelId) return;

    this.$year.options[0].textContent = vfT('loading');

    try {
      const { data } = await vfFetch(
        '/BaseVehicle?filter[makeId][_eq]=' + makeId +
        '&filter[modelId][_eq]=' + modelId +
        '&limit=-1&meta=filter_count'
      );

      // Mapa yearId → objeto BaseVehicle para recuperar vehicleCode al seleccionar
      this._yearMap = {};
      data.forEach(v => { this._yearMap[v.yearId] = v; });

      const sorted = [...data].sort((a, b) => Number(b.yearId) - Number(a.yearId));
      this.populateSelect(this.$year, sorted.map(v => ({
        value: v.yearId,
        label: v.yearId
      })));
      this.$year.disabled = false;
    } catch (err) {
      console.error('[VehicleFinder] Error cargando años:', err);
      this.showError();
    }
  }

  // ─── Eventos ─────────────────────────────────────────────────────────────

  bindEvents() {
    this.$make.addEventListener('change', () => {
      this.hideError();
      const opt = this.$make.selectedOptions[0];
      this.state.makeId    = opt?.value ? Number(opt.value) : null;
      this.state.makeName  = opt?.value ? opt.textContent.trim() : '';
      this.state.modelId   = null;
      this.state.modelName = '';
      this.state.year      = '';
      this.state.vehicleCode = '';
      this.loadModels(this.state.makeId);
    });

    this.$model.addEventListener('change', () => {
      this.hideError();
      const opt = this.$model.selectedOptions[0];
      this.state.modelId   = opt?.value ? Number(opt.value) : null;
      this.state.modelName = opt?.value ? opt.textContent.trim() : '';
      this.state.year      = '';
      this.state.vehicleCode = '';
      this.loadYears(this.state.makeId, this.state.modelId);
    });

    this.$year.addEventListener('change', () => {
      this.hideError();
      const yearId = this.$year.value;
      this.state.year        = yearId;
      this.state.vehicleCode = yearId ? (this._yearMap[yearId]?.code || '') : '';
      this.$submit.disabled  = !yearId;
    });

    this.$submit.addEventListener('click', () => this.handleSubmit());
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  handleSubmit() {
    this.setLoading(true);
    this.hideError();

    const query = [this.state.makeName, this.state.modelName, this.state.year]
      .filter(Boolean)
      .join(' ');

    const params = new URLSearchParams({ q: query, type: 'product' });

    window.location.href = (this.config.searchUrl || '/search') + '?' + params.toString();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  populateSelect(select, items) {
    const placeholder = select.options[0]?.value === '' ? select.options[0] : null;
    select.innerHTML = '';
    if (placeholder) {
      placeholder.textContent = placeholder.dataset.placeholder || placeholder.textContent;
      select.add(placeholder);
    }
    items.forEach(({ value, label }) => select.add(new Option(label, value)));
  }

  resetSelect(select, placeholderText) {
    select.innerHTML = '';
    const opt = new Option(placeholderText, '');
    select.add(opt);
  }

  setLoading(isLoading) {
    const btnText    = this.$submit.querySelector('.vehicle-finder__btn-text');
    const btnSpinner = this.$submit.querySelector('.vehicle-finder__btn-spinner');
    this.$submit.disabled  = isLoading;
    this.$submit.ariaBusy  = String(isLoading);
    if (btnText)    btnText.hidden    = isLoading;
    if (btnSpinner) btnSpinner.hidden = !isLoading;
  }

  showError() { if (this.$error) this.$error.hidden = false; }
  hideError() { if (this.$error) this.$error.hidden = true;  }
}

customElements.define('vehicle-finder', VehicleFinder);